/**
 * Session state: who is signed in, and what they may do.
 *
 * This is the one slice that is not purely UI state, and it is here rather than
 * in TanStack Query for a specific reason: **routing depends on it
 * synchronously.** `ProtectedRoute` has to decide render-or-redirect on the
 * first paint, and a permission gate has to answer `can('leads','create')`
 * during render. A query hook that starts in a loading state cannot answer
 * either without flashing the wrong thing.
 *
 * The server data it holds — `/auth/me` — is fetched once at boot and refreshed
 * on demand, not polled. Permissions change rarely, and when they do the change
 * is announced by a 403 on the next call rather than by a background refetch.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authApi from '../../api/auth.api.js';
import {
  clearStoredSession,
  readStoredSession,
  sessionFromLogin,
  writeStoredSession,
} from '../../api/tokenStore.js';

/**
 * Sign in.
 *
 * Resolves with one of three outcomes rather than throwing on the two that are
 * not a signed-in session, because all three are legitimate answers to a correct
 * password and the login screen has to render a different thing for each:
 *
 *   { outcome: 'authenticated' } · { outcome: 'mfa', … } · { outcome: 'sso', … }
 */
export const signIn = createAsyncThunk('auth/signIn', async (credentials, { rejectWithValue }) => {
  try {
    const body = await authApi.login(credentials);

    if (body.mfa_required) {
      return { outcome: 'mfa', challengeToken: body.challenge_token, factors: body.factors, expiresIn: body.expires_in };
    }
    if (body.sso_required) {
      return { outcome: 'sso', provider: body.provider, authorizeUrl: body.authorize_url };
    }

    writeStoredSession(sessionFromLogin(body));
    return { outcome: 'authenticated', body };
  } catch (error) {
    // Carried as a plain object: an Error instance is not serialisable and
    // Redux Toolkit will warn about putting one in the store.
    return rejectWithValue({
      code: error.code,
      message: error.message,
      details: error.details ?? [],
      payload: error.payload ?? {},
    });
  }
});

/**
 * Load `/auth/me` — the bootstrap call.
 *
 * Runs on every app start where a token exists, because roles and screens can
 * have changed since the token was minted. A failure here is not necessarily a
 * dead session; the axios interceptor has already tried to refresh, so if it
 * still fails the session really is over.
 */
export const loadSession = createAsyncThunk('auth/loadSession', async (_, { rejectWithValue }) => {
  try {
    return await authApi.getMe();
  } catch (error) {
    return rejectWithValue({ code: error.code, message: error.message });
  }
});

/** Sign out. Best-effort on the server; local state is cleared regardless. */
export const signOut = createAsyncThunk('auth/signOut', async (allDevices = false) => {
  try {
    await authApi.logout(allDevices);
  } catch {
    // A network failure must not strand someone in a session they asked to end.
  }
  clearStoredSession();
});

const initialState = {
  /** 'unknown' until the boot check finishes — routes must wait, not guess. */
  status: readStoredSession() ? 'unknown' : 'anonymous',
  user: null,
  organization: null,
  roles: [],
  /** Screen key → { operations: string[], data_scope: string, … } */
  screens: {},
  /** entity_key → field_key → 'hidden'|'read'|'write' */
  fieldPermissions: {},
  preferences: {},
  landingScreen: null,
  session: null,
  error: null,
  /** Set when login returned a challenge instead of a session. */
  mfaChallenge: null,
  ssoRedirect: null,
  /**
   * True only when `sessionLost` fired — a refresh failure or a revoked/invalid
   * token, never a user-initiated `signOut`. This is what tells the app to show
   * the "Session expired" modal instead of just quietly landing on `/login`.
   */
  sessionExpired: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Called by the axios interceptor when a session can no longer be refreshed. */
    sessionLost(state) {
      Object.assign(state, initialState, { status: 'anonymous', sessionExpired: true });
    },
    /** Called once the "Session expired" modal has sent the user to sign in again. */
    dismissSessionExpired(state) {
      state.sessionExpired = false;
    },
    clearAuthError(state) {
      state.error = null;
    },
    clearChallenge(state) {
      state.mfaChallenge = null;
      state.ssoRedirect = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.status = 'signing-in';
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        const { outcome } = action.payload;
        if (outcome === 'mfa') {
          state.status = 'anonymous';
          state.mfaChallenge = action.payload;
          return;
        }
        if (outcome === 'sso') {
          state.status = 'anonymous';
          state.ssoRedirect = action.payload;
          return;
        }
        // The full picture still comes from /auth/me; login gives enough to
        // route immediately so the user is not held on a spinner twice.
        const { body } = action.payload;
        state.status = 'authenticated';
        state.user = body.user;
        state.roles = body.roles;
        state.landingScreen = body.landing_screen;
        state.session = body.session;
        state.mustChangePassword = body.must_change_password;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.status = 'anonymous';
        state.error = action.payload ?? { code: 'unknown_error', message: 'Sign-in failed.' };
      })

      .addCase(loadSession.pending, (state) => {
        if (state.status !== 'authenticated') state.status = 'loading';
      })
      .addCase(loadSession.fulfilled, (state, action) => {
        const me = action.payload;
        state.status = 'authenticated';
        state.user = me.user;
        state.organization = me.organization;
        state.roles = me.roles;
        state.session = me.session;
        state.preferences = me.preferences ?? {};
        state.landingScreen = me.landing_screen;
        state.fieldPermissions = me.field_permissions ?? {};
        // Keyed for O(1) permission checks during render.
        state.screens = Object.fromEntries((me.screens ?? []).map((s) => [s.key, s]));
        state.error = null;
      })
      .addCase(loadSession.rejected, (state, action) => {
        state.status = 'anonymous';
        state.error = action.payload ?? null;
        clearStoredSession();
      })

      .addCase(signOut.fulfilled, (state) => {
        Object.assign(state, initialState, { status: 'anonymous' });
      });
  },
});

export const { sessionLost, dismissSessionExpired, clearAuthError, clearChallenge } = authSlice.actions;
export default authSlice.reducer;

/* -------------------------------------------------------------------------- */
/* Selectors                                                                   */
/* -------------------------------------------------------------------------- */

export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectScreens = (state) => state.auth.screens;
export const selectSessionExpired = (state) => state.auth.sessionExpired;

/**
 * May the current user perform `operation` on `screen`?
 *
 * This is the client half of a two-gate model and it is **advisory only**. It
 * decides whether to render a button; the API asks `iam.can()` again and
 * PostgreSQL row-level security filters independently. Never treat a `true` here
 * as authorisation — its job is to avoid showing a control that would 403.
 *
 * @param {object} state
 * @param {string} screen  e.g. 'leads'
 * @param {string} operation e.g. 'create'
 */
export const selectCan = (state, screen, operation) =>
  Boolean(state.auth.screens[screen]?.operations?.includes(operation));

/**
 * The widest data scope the user holds for a (screen, operation) pair.
 * Returns 'none' when nothing is granted.
 */
export const selectScope = (state, screen) => state.auth.screens[screen]?.data_scope ?? 'none';
